import { appendPersistentMemory } from '@affine/core/blocksuite/ai/cao-oi-gemini-bridge';
import { getTranscriptionApiKey } from '@affine/core/blocksuite/ai/groq-stream';

export interface RecordingItem {
  id: string;
  title: string;
  createdAt: number;
  durationSeconds: number;
  sizeBytes: number;
  transcript: string;
  hasAudio: boolean;
}

export const DB_NAME = 'cao_oi_recordings_audio_db';
export const STORE_NAME = 'audio_blobs';
export const STORAGE_KEY = 'cao_oi_recordings_archive_v2';

export function openAudioDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveAudioBlob(id: string, blob: Blob): Promise<void> {
  try {
    const db = await openAudioDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(blob, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('IndexedDB saveAudioBlob failed:', err);
  }
}

export async function getAudioBlob(id: string): Promise<Blob | null> {
  try {
    const db = await openAudioDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('IndexedDB getAudioBlob failed:', err);
    return null;
  }
}

export async function deleteAudioBlob(id: string): Promise<void> {
  try {
    const db = await openAudioDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('IndexedDB deleteAudioBlob failed:', err);
  }
}

export function insertTranscriptIntoActiveDoc(text: string) {
  try {
    const host =
      (typeof document !== 'undefined' &&
        (document.querySelector('editor-host') as any)) ||
      (typeof document !== 'undefined' &&
        (document.querySelector('affine-edgeless-root') as any));
    const doc = host?.doc;
    if (doc) {
      const notes = doc.getBlocksByFlavour('affine:note');
      if (notes && notes[0]?.id) {
        const time = new Date().toLocaleTimeString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit',
        });
        const entry = `🎙️ [Bài giảng ${time}]: ${text}`;
        doc.addBlock(
          'affine:paragraph',
          { type: 'text', text: new doc.Text(entry) },
          notes[0].id
        );
      }
    }
  } catch (e) {
    console.warn('[CaoOi] Note insertion skipped:', e);
  }
}

export interface GlobalRecordingState {
  isRecording: boolean;
  isTranscribing: boolean;
  recordingSeconds: number;
  statusMessage: string;
}

class GlobalRecordingManager {
  private state: GlobalRecordingState = {
    isRecording: false,
    isTranscribing: false,
    recordingSeconds: 0,
    statusMessage: '',
  };

  private readonly listeners = new Set<() => void>();
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private timerId: number | null = null;
  private chunks: BlobPart[] = [];
  private backgroundTimerId: number | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', () => {
        if (document.hidden && this.state.isRecording) {
          // If tab is in background for over 5 minutes, auto-stop to save MacBook battery
          this.backgroundTimerId = window.setTimeout(() => {
            if (this.state.isRecording) {
              this.stopRecording('background-timeout').catch(console.error);
            }
          }, 300000);
        } else {
          if (this.backgroundTimerId) {
            clearTimeout(this.backgroundTimerId);
            this.backgroundTimerId = null;
          }
        }
      });

      window.addEventListener('beforeunload', () => {
        if (this.state.isRecording) {
          this.stopRecording('beforeunload').catch(console.error);
        }
      });
    }
  }

  public getState(): GlobalRecordingState {
    return this.state;
  }

  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify() {
    this.listeners.forEach(fn => fn());
  }

  public async startRecording(): Promise<void> {
    if (this.state.isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      this.mediaStream = stream;
      this.chunks = [];

      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        this.audioCtx = new AudioCtxClass();
      }

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = e => {
        if (e.data && e.data.size > 0) {
          this.chunks.push(e.data);
        }
      };

      recorder.start(1000);
      this.mediaRecorder = recorder;

      this.state = {
        ...this.state,
        isRecording: true,
        recordingSeconds: 0,
        statusMessage: 'Đang ghi âm bài giảng...',
      };
      this.notify();

      if (this.timerId) clearInterval(this.timerId);
      this.timerId = window.setInterval(() => {
        // Hard safety cap: 90 minutes max continuous lecture recording
        if (this.state.recordingSeconds >= 5400) {
          this.stopRecording('max-duration').catch(console.error);
          return;
        }
        this.state = {
          ...this.state,
          recordingSeconds: this.state.recordingSeconds + 1,
        };
        this.notify();
      }, 1000);
    } catch (err) {
      console.error('Microphone access failed:', err);
      alert('Không thể mở Microphone. Vui lòng cấp quyền truy cập microphone trên trình duyệt!');
    }
  }

  public async stopRecording(reason?: string, duration?: number): Promise<void> {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.backgroundTimerId) {
      clearTimeout(this.backgroundTimerId);
      this.backgroundTimerId = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {}
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
    if (this.audioCtx) {
      try {
        await this.audioCtx.close();
      } catch {}
      this.audioCtx = null;
    }

    const recordedSeconds = duration
      ? Math.max(1, Math.round(duration / 1000))
      : this.state.recordingSeconds || 1;

    if (reason === 'cancel') {
      this.chunks = [];
      this.state = {
        isRecording: false,
        isTranscribing: false,
        recordingSeconds: 0,
        statusMessage: 'Đã huỷ bản thu âm.',
      };
      this.notify();
      return;
    }

    this.state = {
      isRecording: false,
      isTranscribing: true,
      recordingSeconds: 0,
      statusMessage: 'Đang chuyển đổi giọng nói thành văn bản với Whisper Large V3...',
    };
    this.notify();

    // Give 400ms for final data chunk to flush
    setTimeout(() => {
      void (async () => {
        const fullBlob = new Blob(this.chunks, { type: 'audio/webm' });
      const sizeBytes = fullBlob.size;
      const id = `${Date.now()}`;
      const now = new Date();
      const title = `Bài giảng CEO - ${now.toLocaleDateString('vi-VN')} ${now.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;

      let transcript = '';

      try {
        const groqKey = getTranscriptionApiKey();
        if (groqKey && fullBlob.size > 2000) {
          const formData = new FormData();
          formData.append('file', fullBlob, 'recording.webm');
          formData.append('model', 'whisper-large-v3');
          formData.append('language', 'vi');
          formData.append(
            'prompt',
            'Chuyển đoạn ghi âm bài giảng CEO/Quản trị này sang tiếng Việt chuẩn xác. Giữ đúng các thuật ngữ: Situational Leadership, Push & Pull, FACT Feedback, C-V-P, OKR/KPI, Storyselling. Chỉ trả về lời nói nghe được.'
          );

          const res = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
            method: 'POST',
            headers: { Authorization: `Bearer ${groqKey}` },
            body: formData,
          });

          if (res.ok) {
            const data = await res.json();
            transcript = data?.text?.trim() || '';
          } else {
            console.error('Whisper API returned error:', await res.text());
          }
        }
      } catch (e) {
        console.error('Transcription error:', e);
      }

      if (!transcript) {
        transcript = `[Ghi âm hoàn tất lúc ${now.toLocaleTimeString(
          'vi-VN'
        )}. Chưa nhận diện được giọng nói rõ ràng hoặc chưa có API key].`;
      } else {
        appendPersistentMemory(
          `[Nội dung bài giảng mới ghi âm]: ${transcript.slice(0, 500)}...`
        );
        insertTranscriptIntoActiveDoc(transcript);
      }

      // Save audio blob in IndexedDB
      await saveAudioBlob(id, fullBlob);

      // Save recording item in localStorage archive
      const newItem: RecordingItem = {
        id,
        title,
        createdAt: Date.now(),
        durationSeconds: recordedSeconds,
        sizeBytes,
        transcript,
        hasAudio: true,
      };

      try {
        const existing = localStorage.getItem(STORAGE_KEY);
        const list: RecordingItem[] = existing ? JSON.parse(existing) : [];
        list.unshift(newItem);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (err) {
        console.warn('Failed to update localStorage recordings:', err);
      }

      // Dispatch event to notify Recordings page
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('cao-oi-recordings-updated', { detail: { item: newItem } })
        );
      }

      // Auto-backup to Google Drive if enabled
      import('@affine/core/desktop/components/google-drive-sync/google-drive-service')
        .then(({ autoBackupSingleRecording }) => {
          autoBackupSingleRecording(newItem, fullBlob).catch(err => {
            console.warn('Auto-backup to Google Drive failed:', err);
          });
        })
        .catch(() => {});

        this.state = {
          ...this.state,
          isTranscribing: false,
          statusMessage: '',
        };
        this.notify();
      })().catch(console.error);
    }, 400);
  }
}

export const globalRecordingManager = new GlobalRecordingManager();
